
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { AuthForm } from "@/components/auth/AuthForm";
import { GoogleCompanyPrompt } from "@/components/auth/GoogleCompanyPrompt";
import { supabase } from "@/integrations/supabase/client";

const Auth = () => {
  const navigate = useNavigate();
  const {
    loading,
    email,
    setEmail,
    password,
    setPassword,
    companyName,
    setCompanyName,
    isLogin,
    setIsLogin,
    handleAuth,
    handleGoogleSignIn,
    teamMemberId,
    appsumoCode,
    showGoogleCompanyPrompt,
    submitGoogleCompanyName,
    handleGoogleUserTeamInvitation,
    googleCompanyName,
    setGoogleCompanyName
  } = useAuth();

  // Enable dark mode for auth page
  useEffect(() => {
    document.documentElement.classList.add("admin-dark");
    return () => {
      // Don't remove dark mode on cleanup - it will be managed by the next page
    };
  }, []);

  // Check if user is already logged in on page load
  useEffect(() => {
    const checkAuthStatus = async () => {
      const { data } = await supabase.auth.getSession();
      // Only redirect to admin if user is authenticated AND not showing company prompt
      // AND not handling team invitation or appsumo code
      if (data.session && !showGoogleCompanyPrompt && !teamMemberId && !appsumoCode) {
        navigate("/admin");
      }
    };

    checkAuthStatus();
  }, [showGoogleCompanyPrompt]); // Add showGoogleCompanyPrompt as a dependency

  // Check if the user has logged in with Google and has a team invitation
  useEffect(() => {
    const checkGoogleTeamInvite = async () => {
      if (teamMemberId) {
        const { data } = await supabase.auth.getSession();
        // If user is logged in through Google and has a team invitation
        if (data.session?.provider_token && teamMemberId) {
          handleGoogleUserTeamInvitation();
        }
      }
    };
    
    checkGoogleTeamInvite();
  }, [teamMemberId]);

  // Handle Google sign-in with team invitation
  const handleGoogleSignInWithInvitation = () => {
    return handleGoogleSignIn(teamMemberId);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      {showGoogleCompanyPrompt ? (
        <GoogleCompanyPrompt
          companyName={googleCompanyName || ""}
          setCompanyName={setGoogleCompanyName}
          onSubmit={submitGoogleCompanyName}
          loading={loading}
        />
      ) : (
        <AuthForm 
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          companyName={companyName}
          setCompanyName={setCompanyName}
          isLogin={isLogin}
          setIsLogin={setIsLogin}
          loading={loading}
          handleAuth={handleAuth}
          handleGoogleSignIn={handleGoogleSignInWithInvitation}
          teamMemberId={teamMemberId}
          appsumoCode={appsumoCode}
        />
      )}
    </div>
  );
};

export default Auth;
